import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import './AuthGate.css';

interface AuthGateProps {
  children: React.ReactNode;
}

/**
 * Access-control wrapper that shows sign-in, access-denied, or loading states depending on auth status.
 * Renders children only when the user is authenticated and allowed.
 * @param {AuthGateProps} props
 * @param {React.ReactNode} props.children - Content to render when access is granted.
 */
const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const { user, isAllowed, loading, signIn } = useAuth();

  if (loading) {
    return (
      <div className="auth-gate">
        <div className="auth-gate-icon">&#x23F3;</div>
        <p className="auth-gate-loading">Checking access...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-gate">
        <div className="auth-gate-icon">&#x1F512;</div>
        <h3>Sign in required</h3>
        <p>This content requires authentication. Sign in with your Google account to continue.</p>
        <button className="auth-gate-signin" onClick={signIn}>
          Sign in with Google
        </button>
      </div>
    );
  }

  if (!isAllowed) {
    return (
      <div className="auth-gate">
        <div className="auth-gate-user">
          {user.photoURL && <img className="auth-gate-user-avatar" src={user.photoURL} alt="" />}
          <span className="auth-gate-user-name">{user.displayName}</span>
        </div>
        <div className="auth-gate-icon">&#x1F6AB;</div>
        <h3>Access denied</h3>
        <p className="auth-gate-denied">Your account does not have access to this content.</p>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthGate;
